import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { type UserApprovalFilters, type UserSession, userApprovalService } from "../services/user-approval-service";

const QUERY_KEY = "user-approvals";

export const useUserApprovalsQuery = (filters: UserApprovalFilters = {}) => {
	return useQuery({
		queryKey: [QUERY_KEY, filters],
		queryFn: () => userApprovalService.getPendingUsers(filters),
	});
};

export const useAllUsersQuery = (filters: UserApprovalFilters = {}) => {
	return useQuery({
		queryKey: [QUERY_KEY, "all", filters],
		queryFn: () => userApprovalService.getAllUsers(filters),
	});
};

export const useUserQuery = (id: string) => {
	return useQuery({
		queryKey: [QUERY_KEY, id],
		queryFn: () => userApprovalService.findById(id),
		enabled: !!id,
	});
};
export const useApproveUserMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, notes, role }: { userId: string; notes?: string; role?: string }) => userApprovalService.approveUser(userId, notes, role),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("User approved successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to approve user");
		},
	});
};

export const useRejectUserMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, notes }: { userId: string; notes: string }) => userApprovalService.rejectUser(userId, notes),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("User rejected successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to reject user");
		},
	});
};

export const useToggleUserStatusMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (userId: string) => userApprovalService.toggleUserStatus(userId),
		onSuccess: (data: { message: string }) => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success(data.message);
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to toggle user status");
		},
	});
};

export const useUpdateUserRoleMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, role }: { userId: string; role: string }) => userApprovalService.updateUserRole(userId, role),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("User role updated successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to update user role");
		},
	});
};

export const useUserSessionsQuery = (userId: string) => {
	return useQuery<UserSession[]>({
		queryKey: ["user-sessions", userId],
		queryFn: () => userApprovalService.getUserSessions(userId),
	});
};
